import { useCallback } from "react";

import { IsoLocale } from "@simplyk/common";
import { useRouter } from "next/router";

import { useCurrentUserContext } from "../contexts/CurrentUserContext";
import { FRONTEND_URL } from "../routes/routes";

const getDashboardReloadUrl = (path: string, isoLocale?: IsoLocale) =>
  `${FRONTEND_URL}${isoLocale ? "/" + isoLocale : ""}${path}`;

interface RedirectOptions {
  reload?: boolean;
  isoLocale?: IsoLocale;
}

/**
 * Hook for handling redirections in the application.
 * Uses `defaultRedirectUrl` from the backend (via CurrentUserContext) as the default destination.
 */
export const useRedirect = () => {
  const { defaultRedirectUrl } = useCurrentUserContext();
  const router = useRouter();

  /**
   * Redirects to the user's default page (from backend).
   * Falls back to initial defaultRedirectUrl of useCurrentUserContext if no default is set.
   * @returns Promise that resolves when navigation completes (only for client-side navigation)
   */
  const redirectToDefault = useCallback(
    (options?: RedirectOptions): Promise<boolean> | void => {
      const { reload = false, isoLocale } = options || {};

      if (reload) {
        window.location.href = getDashboardReloadUrl(defaultRedirectUrl, isoLocale);
        return;
      }
      return router.push(defaultRedirectUrl);
    },
    [defaultRedirectUrl, router]
  );

  /**
   * Redirects to a specific URL.
   * @returns Promise that resolves when navigation completes (only for client-side navigation)
   */
  const redirectTo = useCallback(
    (url: string, options?: RedirectOptions): Promise<boolean> | void => {
      const { reload = false, isoLocale } = options || {};

      if (reload) {
        window.location.href = getDashboardReloadUrl(url, isoLocale);
        return;
      }
      return router.push(url);
    },
    [router]
  );

  return {
    redirectToDefault,
    redirectTo,
    defaultRedirectUrl,
  };
};
